/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { getHeaders, handleResponse } from '@opengeoweb/api';
import type { Credentials } from '@opengeoweb/authentication';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type {
  Bulletin,
  DiscardDraftNotification,
  EventCategoryParams,
  StreamResponse,
  SWEvent,
  SWNotification,
  TimeseriesParams,
  TimeseriesResponseData,
} from '../../types';

const config = sessionStorageProvider.getConfig();

const spaceweatherBaseUrl = config.GW_SW_BASE_URL || '';

// API

// Bulletin section
export const getBulletinHistoryList = async (
  auth: Credentials,
): Promise<Bulletin[]> =>
  fetch(`${spaceweatherBaseUrl}/bulletin/getBulletinHistory`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<Bulletin[]>;

export const getBulletin = async (auth: Credentials): Promise<Bulletin> =>
  fetch(`${spaceweatherBaseUrl}/bulletin/getBulletin`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<Bulletin>;

// Notification section
export const getEventList = async (
  auth: Credentials,
  params: EventCategoryParams,
): Promise<SWEvent[]> => {
  const URLParams = new URLSearchParams(params as Record<string, string>);
  return fetch(`${spaceweatherBaseUrl}/notification/eventList?${URLParams}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<SWEvent[]>;
};

export const getEvent = async (
  auth: Credentials,
  eventid: string,
): Promise<SWEvent> =>
  fetch(`${spaceweatherBaseUrl}/notification/event/${eventid}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<SWEvent>;

export const getNewNotifications = async (
  auth: Credentials,
): Promise<SWEvent[]> =>
  fetch(`${spaceweatherBaseUrl}/notification/newNotifications`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<SWEvent[]>;

export const postNotification = async (
  auth: Credentials,
  data: SWNotification,
): Promise<void> =>
  fetch(`${spaceweatherBaseUrl}/notification/store`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<void>;

export const postDiscardDraftNotification = async (
  auth: Credentials,
  data: DiscardDraftNotification,
): Promise<void> => {
  const URLParams = new URLSearchParams(data);
  return fetch(`${spaceweatherBaseUrl}/notification/discard?${URLParams}`, {
    headers: getHeaders(auth),
    method: 'POST',
  }).then(handleResponse) as Promise<void>;
};

export const postSetAcknowledged = async (
  auth: Credentials,
  eventid: string,
): Promise<void> =>
  fetch(`${spaceweatherBaseUrl}/notification/acknowledged/${eventid}`, {
    headers: getHeaders(auth),
    method: 'POST',
  }).then(handleResponse) as Promise<void>;

export const postPopulateTemplateContent = async (
  auth: Credentials,
  data: SWNotification,
): Promise<{ title: string; message: string }> =>
  fetch(`${spaceweatherBaseUrl}/notification/populate`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<{ title: string; message: string }>;

export const getStreams = async (
  auth: Credentials,
): Promise<StreamResponse[]> =>
  fetch(`${spaceweatherBaseUrl}/timeseries/streams`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<StreamResponse[]>;

export const getTimeSeries = async (
  auth: Credentials,
  data: TimeseriesParams,
): Promise<TimeseriesResponseData> => {
  const URLParams = new URLSearchParams(data as Record<string, string>);
  return fetch(`${spaceweatherBaseUrl}/timeseries/data?${URLParams}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<TimeseriesResponseData>;
};
